import React from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { autobind } from 'core-decorators';
import { TweenMax, Expo } from 'gsap';
import Swipeable from 'react-swipeable';

import embedActions from '../actions/embedActions';

import styles from '../styles';

const HINT_NAME = 'swipeToNavigate';

class SwipeAnimationOverlay extends React.PureComponent {
  componentDidMount() {
    this.mounted = true;
    this.setInitialVisibility();
    this.toggleAppearanceIfNeeded();
  }

  componentDidUpdate() {
    this.toggleAppearanceIfNeeded();
  }

  @autobind
  onClick() {
    this.props.actions.setHintAsSeen(HINT_NAME);
  }

  @autobind
  onSwiped() {
    this.props.actions.setHintAsSeen(HINT_NAME);
  }

  @autobind
  onRefCallback(el) {
    if (!this.container) {
      this.container = el;
      this.setInitialVisibility();
    }
  }

  @autobind
  onImageRefCallback(el) {
    if (!this.image) {
      this.image = el;
    }
  }

  @autobind
  onTitleRefCallback(el) {
    if (!this.title) {
      this.title = el;
    }
  }

  setInitialVisibility() {
    if (this.mounted && this.container) {
      const shouldShowOnMount = this.shouldShow();
      TweenMax.set(this.container.element, {
        autoAlpha: shouldShowOnMount ? 1 : 0,
      });
    }
  }

  shouldShow() {
    return this.props.showIfNeeded && !this.props.seen;
  }

  toggleAppearanceIfNeeded() {
    if (this.shouldShow()) {
      this.show();
    } else {
      this.hide();
    }
  }

  show() {
    TweenMax.killTweensOf(this.container);
    TweenMax.to(this.container.element, 0.5, {
      autoAlpha: 1,
    });
    this.startImageAnimationIfReady();
  }

  hide() {
    TweenMax.killTweensOf(this.container);
    TweenMax.to(this.container.element, 0.5, {
      autoAlpha: 0,
      onComplete: () => {
        this.container.element.style.display = 'none'; // hide when done (better perf
      },
    });
  }

  startImageAnimationIfReady() {
    if (this.image && this.title && this.mounted) {
      TweenMax.killTweensOf(this.title);
      TweenMax.fromTo(
        this.title,
        1,
        {
          opacity: 0,
          yPercent: 10,
        },
        {
          opacity: 1,
          yPercent: 0,
          delay: 2,
        },
      );
      TweenMax.killTweensOf(this.image);
      TweenMax.fromTo(
        this.image,
        0.5,
        {
          scale: 1.25,
          rotation: 1,
          opacity: 0,
          xPercent: 50,
        },
        {
          scale: 1,
          opacity: 1,
          ease: Expo.easeInOut,
          delay: 0.5,
          onComplete: this.startImageAnimationLoop,
        },
      );
    }
  }

  @autobind
  startImageAnimationLoop() {
    TweenMax.killTweensOf(this.image);
    TweenMax.to(this.image, 1, {
      rotation: -1,
      xPercent: -50,
      ease: Expo.easeInOut,
      yoyo: true,
      repeat: -1,
    });
  }

  render() {
    return (
      <Swipeable
        ref={this.onRefCallback}
        style={styles.fullScreenOverlay}
        onClick={this.onClick}
        onSwiped={this.onSwiped}
      >
        <img
          ref={this.onImageRefCallback}
          src="/assets/img/touch-hand.svg"
          width="86"
          height="124"
          alt=""
        />
        <h1
          ref={this.onTitleRefCallback}
          style={{ ...styles.fullScreenOverlayTitle, marginTop: '25px' }}
        >
          Swipe to navigate
        </h1>
      </Swipeable>
    );
  }
}

SwipeAnimationOverlay.propTypes = {
  actions: PropTypes.objectOf(PropTypes.func).isRequired,
  showIfNeeded: PropTypes.bool.isRequired,
  seen: PropTypes.bool.isRequired,
};

SwipeAnimationOverlay.defaultProps = {};

const mapStateToProps = state => ({
  seen: state.hintsReducer[HINT_NAME] || false,
});

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators(embedActions, dispatch),
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(SwipeAnimationOverlay);
